import React from 'react';
import { Box, Divider, useTheme } from '@mui/material';
import { Typography, Button } from '@fdha/web-ui-library';

import { getStyles } from './DeliveryFooter.styles';

interface DeliveryFooterProps {
  entreesSelected: number;
  entreesTotal: number;
  snacksSelected: number;
  snacksTotal: number;
  hasSides: boolean;
  isValid: boolean;
  isDirty: boolean;
  isSaving: boolean;
  isReadOnly: boolean;
  isCanceled?: boolean;
  showingEmptyState?: boolean;
  allowSelfPreparedMeals?: boolean;
  onSave: () => void;
}

const DeliveryFooter: React.FC<DeliveryFooterProps> = ({
  entreesSelected,
  entreesTotal,
  snacksSelected,
  snacksTotal,
  hasSides,
  isValid,
  isDirty,
  isSaving,
  isReadOnly,
  isCanceled = false,
  showingEmptyState = false,
  allowSelfPreparedMeals = false,
  onSave,
}) => {
  const theme = useTheme();
  const styles = getStyles(theme);

  // Check if selections are within valid range for self-prepared meals
  const isWithinValidRange =
    entreesSelected <= entreesTotal && snacksSelected <= snacksTotal;

  // Calculate if save should be disabled
  const isSaveDisabled = () => {
    // Always disable if no changes or currently saving
    if (!isDirty || isSaving) return true;

    if (allowSelfPreparedMeals) {
      // For self-prepared meals: allow saving if selections are within valid range (0 to required amount)
      return !isWithinValidRange;
    } else {
      // For regular meals: use isValid from context
      return !isValid;
    }
  };

  return (
    <>
      {!showingEmptyState && <Divider sx={styles.divider} />}
      <Box sx={styles.container}>
        {isValid ? (
          <Typography
            variant="body2"
            sx={styles.completionMessage}
            i18nKey="meals:editDelivery.footer.total"
          >
            You have selected all options
          </Typography>
        ) : (
          <Typography
            variant="body2"
            i18nKey="meals:reviewDelivery.footer.amounts.snacks"
            i18nComponents={{
              strongEntree: (
                <Box component="span" sx={styles.summaryNumber}>
                  {entreesSelected}/{entreesTotal}
                </Box>
              ),
              strongSnacks: (
                <Box component="span" sx={styles.summaryNumber}>
                  {snacksSelected}/{snacksTotal}
                </Box>
              ),
            }}
          >
            `${entreesSelected}/${entreesTotal} Entree Dishes, ${snacksSelected}
            /${snacksTotal} Snacks/Sides`
          </Typography>
        )}

        {/* Only show save button for open deliveries */}
        {!isReadOnly && (
          <Button
            variant="contained"
            onClick={onSave}
            disabled={isSaveDisabled()}
            sx={styles.saveButton}
            i18nKey="meals:reviewDelivery.footer.button"
          >
            Save selection
          </Button>
        )}
      </Box>
    </>
  );
};

export default DeliveryFooter;
